import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Calendar, CreditCard, TrendingUp, Shield, FileText, Pencil, AlertTriangle, X } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { BillingDataModal } from "@/components/billing/BillingDataModal";

interface BillingData {
  id: string;
  full_name: string;
  address_street: string;
  address_zip: string;
  address_city: string;
  address_province: string;
  address_country: string;
  fiscal_code: string | null;
  vat_number: string | null;
}

interface SubscriptionData {
  id: string;
  user_id: string;
  product_type: string;
  product_duration: string;
  status: string;
  amount_paid: number;
  currency: string;
  current_period_start: string;
  current_period_end: string;
  stripe_subscription_id: string | null;
  scheduled_product_type: string | null;
  scheduled_change_date: string | null;
  scheduled_amount: number | null;
  cancel_at_period_end: boolean | null;
}

const Subscription = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isLoading, isAuthenticated, userRole, refreshBillingData } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [billingData, setBillingData] = useState<BillingData | null>(null);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [cancellingDowngrade, setCancellingDowngrade] = useState(false);
  
  const [loadingData, setLoadingData] = useState(true);
  const [loadingPortal, setLoadingPortal] = useState(false);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/auth/login");
    }
  }, [isLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (user) {
      fetchSubscriptionData();
    }
  }, [user]);

  const fetchSubscriptionData = async () => {
    if (!user) return;

    try {
      // Get active subscription
      const { data: subData, error: subError } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", user.id)
        .in("status", ["active", "past_due"])
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      if (!subError && subData) {
        setSubscription(subData);
      }

      // Get billing data
      const { data: billData, error: billError } = await supabase
        .from("billing_data")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!billError && billData) {
        setBillingData(billData);
      }

    } catch (error) {
      console.error("Error fetching subscription data:", error);
    } finally {
      setLoadingData(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      active: "bg-success text-dark",
      cancelled: "bg-destructive text-card",
      expired: "bg-muted text-dark",
      past_due: "bg-warning text-dark",
    };

    return badges[status as keyof typeof badges] || "bg-muted text-dark";
  };

  const getPlanName = (type: string) => {
    const names = {
      base: "Base",
      pro: "Pro",
      plus: "Plus",
      spotlight: "Spotlight",
      social_boost: "Social Boost",
    };

    return names[type as keyof typeof names] || type;
  };

  const getDaysRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const handleManageSubscription = async () => {
    setLoadingPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      
      if (error) throw error;
      if (!data?.url) throw new Error("No portal URL received");

      // Apri il portale in una nuova tab
      window.open(data.url, "_blank");
    } catch (error) {
      console.error("Error opening customer portal:", error);
      toast({ title: "Impossibile aprire il portale di gestione. Riprova.", variant: "error" });
    } finally {
      setLoadingPortal(false);
    }
  };

  const handleCancelDowngrade = async () => {
    setCancellingDowngrade(true);
    try {
      const { data, error } = await supabase.functions.invoke("cancel-scheduled-downgrade");
      
      if (error) throw error;

      if (data.success) {
        toast({ title: data.message, variant: "success" });
        fetchSubscriptionData();
      }
    } catch (error: any) {
      console.error("Error cancelling downgrade:", error);
      toast({ title: error.message || "Impossibile annullare il downgrade. Riprova.", variant: "error" });
    } finally {
      setCancellingDowngrade(false);
    }
  };

  if (isLoading || loadingData) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <p className="font-heading text-xl">Caricamento...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-surface">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="mb-4 text-sm">
            <Link to="/dashboard" className="text-muted-foreground hover:text-primary transition-colors">
              Dashboard
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground font-semibold">
              Il tuo abbonamento
            </span>
          </div>

          <div className="flex items-center gap-4 mb-8">
            <button
              onClick={() => navigate("/dashboard")}
              className="p-2 hover:bg-accent transition-colors rounded-lg"
              aria-label={t("common.back")}
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="font-heading font-bold text-4xl text-dark">
              Il tuo abbonamento
            </h1>
          </div>

          {subscription ? (
            <>
              {/* Cancellation Banner */}
              {subscription.cancel_at_period_end && (
                <div className="mb-6 p-6 bg-destructive/20 border-[3px] border-destructive rounded-none shadow-[4px_4px_0px_0px_rgba(220,38,38,0.5)]">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-6 h-6 text-destructive flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-heading font-bold text-lg text-dark mb-1">
                          Abbonamento annullato
                        </h3>
                        <p className="font-body text-dark">
                          Potrai usufruire del piano {getPlanName(subscription.product_type)} fino al{" "}
                          <strong>{new Date(subscription.current_period_end).toLocaleDateString("it-IT")}</strong>.
                          Dopo questa data perderai l'accesso alle funzionalità premium.
                        </p>
                      </div>
                    </div>
                    <BrutalButton
                      variant="primary"
                      size="sm"
                      onClick={handleManageSubscription}
                      disabled={loadingPortal}
                      className="shrink-0"
                    >
                      {loadingPortal ? "Apertura..." : "Riattiva abbonamento"}
                    </BrutalButton>
                  </div>
                </div>
              )}

              {/* Scheduled Downgrade Banner */}
              {subscription.scheduled_product_type && (
                <div className="mb-6 p-6 bg-warning/20 border-[3px] border-warning rounded-none shadow-[4px_4px_0px_0px_rgba(234,179,8,0.5)]">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-6 h-6 text-warning flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-heading font-bold text-lg text-dark mb-1">
                          {t('subscription.downgrade.scheduledTitle')}
                        </h3>
                        <p className="font-body text-dark">
                          {t('subscription.downgrade.scheduledDesc', {
                            date: new Date(subscription.scheduled_change_date!).toLocaleDateString("it-IT"),
                            plan: getPlanName(subscription.scheduled_product_type),
                            amount: subscription.scheduled_amount,
                          })}
                        </p>
                      </div>
                    </div>
                    <BrutalButton
                      variant="outline"
                      size="sm"
                      onClick={handleCancelDowngrade}
                      disabled={cancellingDowngrade}
                      className="flex items-center gap-2 shrink-0"
                    >
                      <X className="w-4 h-4" />
                      {cancellingDowngrade ? t('common.loading') : t('subscription.downgrade.cancel')}
                    </BrutalButton>
                  </div>
                </div>
              )}

              {userRole === 'admin' && subscription.stripe_subscription_id?.startsWith('admin_lifetime_') && (
                <div className="mb-6 p-6 bg-gradient-to-r from-purple-50 to-purple-100 border-[3px] border-purple-600 rounded-none shadow-[4px_4px_0px_0px_rgba(126,34,206,0.8)]">
                  <div className="flex items-center gap-3 mb-2">
                    <Shield className="w-6 h-6 text-purple-600 flex-shrink-0" />
                    <h3 className="font-heading font-bold text-xl text-purple-900">
                      Piano Plus Lifetime - Account Amministratore
                    </h3>
                  </div>
                  <p className="font-body text-purple-800">
                    Come amministratore hai accesso illimitato e gratuito a tutte le funzionalità Plus.
                  </p>
                </div>
              )}
              
              <BrutalCard className="mb-8">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="font-heading font-bold text-3xl text-dark">
                        {getPlanName(subscription.product_type)}
                      </h2>
                      <span
                        className={`px-3 py-1 font-heading font-bold text-xs uppercase border-[3px] border-dark ${getStatusBadge(
                          subscription.status
                        )}`}
                      >
                        {subscription.status}
                      </span>
                    </div>
                    <p className="font-body text-muted-foreground">
                      €{subscription.amount_paid} / {subscription.product_duration === "annual" ? "anno" : "28 giorni"}
                    </p>
                  </div>

                  <TrendingUp className="w-12 h-12 text-primary" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3">
                    <Calendar className="w-6 h-6 text-primary flex-shrink-0" />
                    <div>
                      <p className="font-heading font-bold text-sm uppercase mb-1">
                        {subscription.product_duration === "one_time_28_days"
                          ? "Giorni rimanenti"
                          : "Prossimo rinnovo"}
                      </p>
                      <p className="font-body text-lg">
                        {subscription.product_duration === "one_time_28_days"
                          ? `${getDaysRemaining(subscription.current_period_end)} giorni`
                          : new Date(subscription.current_period_end).toLocaleDateString("it-IT")}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CreditCard className="w-6 h-6 text-primary flex-shrink-0" />
                    <div>
                      <p className="font-heading font-bold text-sm uppercase mb-1">
                        Data attivazione
                      </p>
                      <p className="font-body text-lg">
                        {new Date(subscription.current_period_start).toLocaleDateString("it-IT")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 mt-8">
                  <BrutalButton
                    variant="outline"
                    onClick={() => navigate("/pricing")}
                  >
                    Cambia piano
                  </BrutalButton>
                  {subscription.stripe_subscription_id && 
                   !subscription.stripe_subscription_id.startsWith('admin_lifetime_') && (
                    <BrutalButton
                      variant="outline"
                      onClick={handleManageSubscription}
                      disabled={loadingPortal}
                    >
                      {loadingPortal ? "Apertura..." : "Gestisci abbonamento"}
                    </BrutalButton>
                  )}
                </div>
              </BrutalCard>

              {/* Payment History Section */}
              {subscription.stripe_subscription_id && (
                subscription.stripe_subscription_id.startsWith('admin_lifetime_') ? (
                  <BrutalCard className="p-6 bg-success/20">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-success border-[3px] border-dark flex items-center justify-center">
                        <Shield className="w-6 h-6 text-dark" />
                      </div>
                      <div>
                        <h2 className="font-heading font-bold text-xl text-dark mb-1">
                          Abbonamento Lifetime
                        </h2>
                        <p className="font-body text-muted-foreground">
                          Il tuo abbonamento è gratuito e permanente. Non sono previsti pagamenti.
                        </p>
                      </div>
                    </div>
                  </BrutalCard>
                ) : (
                  <BrutalCard className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="font-heading font-bold text-xl text-dark mb-2">
                          Storico pagamenti
                        </h2>
                        <p className="font-body text-muted-foreground">
                          Visualizza tutte le tue fatture e pagamenti su Stripe
                        </p>
                      </div>
                      <BrutalButton
                        variant="outline"
                        onClick={handleManageSubscription}
                        disabled={loadingPortal}
                      >
                        {loadingPortal ? "Apertura..." : "Apri storico"}
                      </BrutalButton>
                    </div>
                  </BrutalCard>
                )
              )}

              {/* Billing Data Section - Sempre visibile per tutti */}
              <BrutalCard className="p-6 mt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-accent border-[3px] border-dark flex items-center justify-center">
                      <FileText className="w-5 h-5 text-dark" />
                    </div>
                    <div>
                      <h2 className="font-heading font-bold text-xl text-dark">
                        Dati di fatturazione
                      </h2>
                      <p className="font-body text-sm text-muted-foreground">
                        {subscription?.stripe_subscription_id?.startsWith('admin_lifetime_')
                          ? "Inserisci i tuoi dati per eventuali acquisti futuri (es. Spotlight)"
                          : "Dati utilizzati per l'emissione delle fatture"}
                      </p>
                    </div>
                  </div>
                  <BrutalButton
                    variant="outline"
                    size="sm"
                    onClick={() => setShowBillingModal(true)}
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    {billingData ? "Modifica" : "Aggiungi"}
                  </BrutalButton>
                </div>

                {billingData ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-muted/30 p-4 border-2 border-dark">
                    <div>
                      <p className="font-heading font-bold text-xs uppercase text-muted-foreground mb-1">
                        Intestatario
                      </p>
                      <p className="font-body">{billingData.full_name}</p>
                    </div>
                    <div>
                      <p className="font-heading font-bold text-xs uppercase text-muted-foreground mb-1">
                        Indirizzo
                      </p>
                      <p className="font-body">
                        {billingData.address_street}, {billingData.address_zip} {billingData.address_city} ({billingData.address_province})
                      </p>
                    </div>
                    {billingData.fiscal_code && (
                      <div>
                        <p className="font-heading font-bold text-xs uppercase text-muted-foreground mb-1">
                          Codice Fiscale
                        </p>
                        <p className="font-body font-mono">{billingData.fiscal_code}</p>
                      </div>
                    )}
                    {billingData.vat_number && (
                      <div>
                        <p className="font-heading font-bold text-xs uppercase text-muted-foreground mb-1">
                          Partita IVA
                        </p>
                        <p className="font-body font-mono">IT{billingData.vat_number}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className={`p-4 border-2 border-dark ${subscription?.stripe_subscription_id?.startsWith('admin_lifetime_') ? 'bg-accent/20' : 'bg-warning/20'}`}>
                    <p className="font-body text-muted-foreground">
                      {subscription?.stripe_subscription_id?.startsWith('admin_lifetime_')
                        ? "Nessun dato di fatturazione inserito. Inseriscili ora per essere pronto a ricevere fatture per eventuali acquisti futuri."
                        : "Nessun dato di fatturazione inserito. Clicca su \"Aggiungi\" per inserire i tuoi dati fiscali."}
                    </p>
                  </div>
                )}
              </BrutalCard>
            </>
          ) : (
            <BrutalCard className="text-center py-12">
              <h2 className="font-heading font-bold text-2xl text-dark mb-4">
                Nessun abbonamento attivo
              </h2>
              <p className="font-body text-muted-foreground mb-8">
                Scegli un piano per iniziare a promuovere la tua attività
              </p>
              <BrutalButton onClick={() => navigate("/pricing")}>
                Scopri i nostri piani
              </BrutalButton>
            </BrutalCard>
          )}

          {/* Billing Data Modal */}
          <BillingDataModal
            open={showBillingModal}
            onClose={() => setShowBillingModal(false)}
            onSuccess={() => {
              setShowBillingModal(false);
              fetchSubscriptionData();
              refreshBillingData();
            }}
            editMode={true}
            existingData={billingData}
          />
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Subscription;
